// Configuration de l'API
// BEST PRACTICE: Utiliser l'utilitaire centralisé pour éviter les erreurs de construction d'URL
import { getApiBaseUrl as getBaseUrl, buildApiUrl, buildApiUrlWithParams } from '../utils/apiUrl';

// Réexporter pour compatibilité avec le code existant
const getApiBaseUrl = getBaseUrl;

// Utiliser un getter pour que la détection se fasse au runtime
export const API_CONFIG = {
  get BASE_URL() {
    return getApiBaseUrl();
  },
  TIMEOUT: 10000,
};

// Endpoints de l'API
export const API_ENDPOINTS = {
  // ========== AUTH ==========
  LOGIN: '/users/auth/login/',
  LOGOUT: '/users/auth/logout/',
  REFRESH_TOKEN: '/users/auth/refresh-token/',
  CURRENT_USER: '/users/me/',  // Corrigé: /users/current/ n'existe pas
  USER_PROFILE: '/users/me/',
  
  // ========== USERS ==========
  USERS: '/users/',
  USER_STATS: '/users/stats/',
  USER_SEARCH: '/users/search/',
  
  // ========== DEPARTMENTS ==========
  DEPARTMENTS: '/users/departments/',
  
  // ========== DOCUMENTS ==========
  DOCUMENTS: '/documents/documents/',
  DOCUMENT_STATS: '/documents/documents/stats/',
  DOCUMENT_RECENT: '/documents/documents/recent/',
  DOCUMENT_FAVORITES: '/documents/documents/favorites/',
  DOCUMENT_DOMAINS: '/documents/domains/',
  DOCUMENT_TYPES: '/documents/types/',
  DOCUMENT_SUBTYPES: '/documents/subtypes/',
  USECASE_TEMPLATES: '/documents/usecase-templates/',
  USECASE_TEMPLATE_DETAIL: (id: string | number) => `/documents/usecase-templates/${id}/`,
  USECASE_CATEGORIES: '/documents/usecase-categories/',
  USECASE_CATEGORY_DETAIL: (id: string | number) => `/documents/usecase-categories/${id}/`,
  
  // Actions sur documents
  DOCUMENT_DOWNLOAD: (id: string) => `/documents/documents/${id}/download/`,
  DOCUMENT_VIEW: (id: string) => `/documents/documents/${id}/view/`,
  DOCUMENT_TOGGLE_FAVORITE: (id: string) => `/documents/documents/${id}/toggle_favorite/`,
  
  // ========== PROJECTS ==========
  PROJECTS: '/projects/projects/',
  PROJECT_STATS: '/projects/projects/stats/',
  PROJECT_MY: '/projects/projects/my_projects/',
  TEAMS: '/projects/teams/',
  TEAM_STATS: '/projects/teams/stats/',
  
  // Actions sur projets
  PROJECT_DOCUMENTS: (id: string) => `/projects/projects/${id}/documents/`,
  PROJECT_ADD_DOCUMENT: (id: string) => `/projects/projects/${id}/add_document/`,
  PROJECT_REMOVE_DOCUMENT: (id: string) => `/projects/projects/${id}/remove_document/`,
  PROJECT_ADD_MEMBER: (id: string) => `/projects/projects/${id}/add_member/`,
  PROJECT_REMOVE_MEMBER: (id: string) => `/projects/projects/${id}/remove_member/`,
  
  // Actions sur équipes
  TEAM_ADD_MEMBER: (id: string) => `/projects/teams/${id}/add_member/`,
  TEAM_REMOVE_MEMBER: (id: string) => `/projects/teams/${id}/remove_member/`,
  
  // ========== SEARCH ==========
  SEARCH: '/search/search/search/',
  SEARCH_SUGGESTIONS: '/search/suggestions/',
  SEARCH_HISTORY: '/search/search/history/',
  SEARCH_STATS: '/search/search/stats/',
  SEARCH_SUMMARIZE: '/search/search/summarize/',
  
  // ========== CLOUD ==========
  CLOUD_PROVIDERS: '/cloud/providers/',
  CLOUD_CONNECTIONS: '/cloud/connections/',
  CLOUD_FILES: '/cloud/files/',
  CLOUD_SYNC_TASKS: '/cloud/sync-tasks/',
  CLOUD_SYNC_LOGS: '/cloud/sync-logs/',
  CLOUD_STATS: '/cloud/stats/',
  CLOUD_OAUTH_AUTH: '/cloud/oauth/auth/',
  CLOUD_OAUTH_CALLBACK: '/cloud/oauth/callback/',
  
  // Actions cloud
  CLOUD_CONNECTION_SYNC: (id: string) => `/cloud/connections/${id}/sync/`,
  CLOUD_CONNECTION_FILES: (id: string) => `/cloud/connections/${id}/files/`,
  CLOUD_CONNECTION_LOGS: (id: string) => `/cloud/connections/${id}/logs/`,
  CLOUD_FILES_UPLOAD: '/cloud/files/upload/',
  
  // ========== PERMISSIONS ==========
  ROLES: '/permissions/roles/',
  USER_ROLES: '/permissions/user-roles/',
  USER_ROLE_SET: '/permissions/user-roles/set_role/',
  PERMISSION_GROUPS: '/permissions/permission-groups/',
  USER_PERMISSIONS: '/permissions/user-permissions/',
  PERMISSIONS: '/permissions/permissions/',
  ACCESS_LOGS: '/permissions/access-logs/',
  PERMISSION_STATS: '/permissions/stats/',
  CAPABILITIES: '/permissions/capabilities/',
  PERMISSION_MATRIX: '/permissions/matrix/',
  MY_PERMISSIONS: '/permissions/me/',
  
  // Actions permissions
  ROLE_PERMISSIONS: (id: string) => `/permissions/roles/${id}/permissions/`,
  ROLE_ASSIGN_PERMISSIONS: (id: string) => `/permissions/roles/${id}/assign_permissions/`,
  ROLE_CAPABILITIES: (id: string) => `/permissions/roles/${id}/capabilities/`,
  ROLE_SYNC: (id: string) => `/permissions/roles/${id}/sync/`,
  ROLE_USERS: (id: string) => `/permissions/roles/${id}/users/`,
  PERMISSIONS_BY_APP: '/permissions/permissions/by_app/',
  
  // ========== EVALUATION ==========
  EVALUATION_PROJECTS: '/evaluation/projects/',
  EVALUATION_LOGFRAMES: '/evaluation/logframes/',
  EVALUATION_ACTION_PLANS: '/evaluation/action-plans/',
  EVALUATION_COLLECTION_FORMS: '/evaluation/collection-forms/',
  EVALUATION_PERFORMANCE_ANALYSES: '/evaluation/performance-analyses/',
  EVALUATION_STRATEGIC_REPORTS: '/evaluation/strategic-reports/',
  EVALUATION_INDICATORS: '/evaluation/indicators/',
  EVALUATION_STATS: '/evaluation/stats/',
  
  // Actions évaluation
  EVALUATION_PROJECT_LOGFRAME: (id: string) => `/evaluation/projects/${id}/logframe/`,
  EVALUATION_PROJECT_ACTION_PLANS: (id: string) => `/evaluation/projects/${id}/action_plans/`,
  EVALUATION_PROJECT_COLLECTION_FORMS: (id: string) => `/evaluation/projects/${id}/collection_forms/`,
  EVALUATION_PROJECT_PERFORMANCE_ANALYSES: (id: string) => `/evaluation/projects/${id}/performance_analyses/`,
  EVALUATION_PROJECT_STRATEGIC_REPORTS: (id: string) => `/evaluation/projects/${id}/strategic_reports/`,
  
  // ========== ANALYTICS ==========
  ANALYTICS_OVERVIEW: '/analytics/overview/',
  ANALYTICS_USER_ACTIVITY: '/analytics/user-activity/',
  ANALYTICS_TOP_PAGES: '/analytics/top-pages/',
  ANALYTICS_DEVICE_STATS: '/analytics/device-stats/',
  ANALYTICS_USER_JOURNEY: '/analytics/user-journey/',
  ANALYTICS_PERFORMANCE_METRICS: '/analytics/performance-metrics/',
  ANALYTICS_RECENT_ACTIVITY: '/analytics/recent-activity/',
};

// Types de réponse API
export interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: string;
  message?: string;
}

export interface PaginatedResponse<T> {
  count: number;
  next: string | null;
  previous: string | null;
  results: T[];
}

// Helper pour construire les URLs correctement
// BEST PRACTICE: Utiliser l'utilitaire centralisé
export const buildUrl = buildApiUrlWithParams;

// Helper pour gérer les erreurs API
export const handleApiError = (error: any): string => {
  if (error.response) {
    // Erreur de réponse du serveur
    return error.response.data?.message || error.response.data?.error || 'Erreur serveur';
  } else if (error.request) {
    // Pas de réponse du serveur
    return 'Impossible de contacter le serveur';
  } else {
    // Erreur lors de la configuration de la requête
    return error.message || 'Erreur inconnue';
  }
};

export default API_CONFIG;
